import WasmController from "react-lib/frameworks/WasmController";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";

// Common
import { SetProperty } from "../../common/CommonInterface";

// API
import DrugOrderItemNedList from "issara-sdk/apis/DrugOrderItemNedList_apps_TPD";
import DrugOrderItemEditNed from "issara-sdk/apis/DrugOrderItemEditNed_apps_TPD";
import ChoiceView from "issara-sdk/apis/ChoiceView_core";

// * Serializer
import DrugOrderItemNedSerializer from "issara-sdk/types/DrugOrderItemNedSerializer_apps_TPD";

export type Props = {

}

export type State = Partial<{
  // seq
  AddNedDrugOrderItemSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    drugOrderList: DrugOrderItemNedSerializer[];
    drugOrderTypeOptions: any[];
    selectedItems: SelectedItemsType[];
    searchDrugOrder: FilterSearchType;
    paginatedItems: PaginatedItemsType;
  }> | null;
}>;

export type SelectedItemsType = Partial<{
  id: number;
  ned: string;
}>;

export type FilterSearchType = Partial<{
  create_date: string | null;
  hn: string | null;
  order_code: string | null;
  order_type: string | null;
  drug_name: string | null;
}>;

export type PaginatedItemsType = Partial<{
  items: DrugOrderItemNedSerializer[];
  page: number;
  pageSize: number;
  total: number;
}>;

type PickedState = Partial<
  Pick<MainState, "buttonLoadCheck" | "errorMessage" | "successMessage" | "masterOptions">
>;

export type PickedProps = Partial<Omit<PickedState, "">>;

// Sequence
type SeqState = {
  sequence: "AddNedDrugOrderItem";
  restart?: boolean;
  clear?: boolean;
};

export type TableHeaderType<T> = {
  Header: string;
  accessor: keyof T;
  width?: number;
};

type ActionType =
  | { action: "SEARCH"; page?: number; pageSize?: number }
  | { action: "CLEAR" }
  | { action: "SAVE"; selectedItems: SelectedItemsType[] };

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;

export type SetProp = SetProperty<State & PickedState>;

type CustomExtract<T, U> = T extends object ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const StateInitial: State & PickedState = {
  // seq
  AddNedDrugOrderItemSequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  successMessage: null,
  errorMessage: null,
};

export type Event =
  | { message: "RunSequence"; params: {} }
  | { message: "GetMasterData"; params: {} };

export type Data = {
  division?: number;
};

export const DataInitial = {};

export const CARD_NAME = "CardAddNedDrugOrderItem";

type Optional<T> = {
  [K in keyof T]: `${typeof CARD_NAME}_${T[K] & string}`;
};

export const ACTIONS = {
  SEARCH: "SEARCH",
  CLEAR: "CLEAR",
  SAVE: "SAVE",
} as const;

export const BTN_ACTS = Object.fromEntries(
  Object.keys(ACTIONS).map((key) => [key, `${CARD_NAME}_${key}`])
) as Optional<typeof ACTIONS>;

const FILTER_INIT: FilterSearchType = {
  create_date: "",
  hn: "",
  order_code: "",
  order_type: "",
  drug_name: "",
};

export const LIST_LIMIT = 20;

const PAGINATED_INIT: PaginatedItemsType = {
  items: [],
  page: 0,
  total: 0,
  pageSize: LIST_LIMIT,
};

const Masters = [
  ["ned", {}],
] as const;

type Handler<P = any, R = any> = (
  controller: WasmController<State & PickedState, Event, Data, Props>,
  params: P
) => R;

// * Hardcode
// export const NedOptions = [
//   { key: 1, value: 1, text: "A. เกิดอาการข้างเคียงในการใช้ยาในบัญชียาหลักแห่งชาติ(ADR) หรือแพ้ยา" },
//   { key: 2, value: 2, text: "B. ผู้ป่วยใช้ยาในบัญชียาหลักแห่งชาติแล้วผลการรักษาไม่บรรลุเป้าหมาย" },
//   { key: 3, value: 3, text: "C. ไม่มียาในบัญชียาหลักแห่งชาติให้ใช้แต่ผู้ป่วยมีข้อบ่งชี้การใช้ยานี้ตามที่ อย. กำหนด" },
//   { key: 4, value: 4, text: "D. มี Contraindication หรือ Drug interaction กับยาในบัญชียาแห่งชาติ" },
//   { key: 5, value: 5, text: "E. ยาในบัญชียาหลักแห่งชาติแพงกว่า" },
//   { key: 6, value: 6, text: "F. ผู้ป่วยแสดงความจำนงต้องการใช้ยา(เบิกไม่ได้)" },
//   { key: 7, value: 7, text: "G. ข้าราชการเบิกไม่ได้ตามระเบียบกระทรวงการคลัง สำหรับหน่วยงานอื่นขึ้นอยู่กับการพิจารณาของต้นสังกัด" },
// ];

/**========================================================================
 **                               START
 *========================================================================**/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({
    message: "GetMasterData",
    params: {
      masters: Masters,
    },
  } as any);

  const drugOrderTypeOptions = await GetDrugOrderTypeOptions(controller, params);

  controller.setState(
    {
      AddNedDrugOrderItemSequence: {
        ...state.AddNedDrugOrderItemSequence,
        sequenceIndex: "Action",
        paginatedItems: {
          page: 0,
          pageSize: LIST_LIMIT,
        },
        drugOrderTypeOptions,
      },
    }
  );
};

/**========================================================================
 **                              ACTIONS
 *========================================================================**/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers = {
    [ACTIONS.SEARCH]: HandleSearchData,
    [ACTIONS.CLEAR]: HandleClearData,
    [ACTIONS.SAVE]: HandleSaveData,
  };

  const action = params.action;

  if (action && actionHandlers[action]) {
    return actionHandlers[action](controller, params as any);
  }
};

const HandleSearchData: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  const searchDrugOrder = state.AddNedDrugOrderItemSequence?.searchDrugOrder;
  const paginatedItems = state.AddNedDrugOrderItemSequence?.paginatedItems;
  const page = (params?.page ?? paginatedItems?.page) || 0;
  const pageSize = (params?.pageSize ?? paginatedItems?.pageSize) || LIST_LIMIT;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SEARCH]: "LOADING",
    },
  });

  const offset = page * pageSize;

  let data = {
    ...searchDrugOrder,
    drug: searchDrugOrder?.drug_name,
    limit: pageSize,
    offset,
  };

  const result = await GetDrugOrderNedList(controller, data);

  if (result?.items) {
    controller.setState({
      AddNedDrugOrderItemSequence: {
        ...state.AddNedDrugOrderItemSequence,
        drugOrderList: result.items,
        paginatedItems: {
          ...paginatedItems,
          page,
          pageSize,
          total: result?.total,
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "SUCCESS",
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SEARCH]: "ERROR",
      },
    });
  }
};

const HandleSaveData: Handler<Params<"SAVE">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [BTN_ACTS.SAVE]: "LOADING",
    },
  });

  const selectedItems = params?.selectedItems || [];

  const [saveResp, saveErr] = await SaveAddNedDrugOrderItem(controller, { selectedItems });

  if (saveResp) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SAVE]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [CARD_NAME]: saveResp?.detail,
      },
    });

    HandleSearchData(controller, { action: "SEARCH", page: 0, pageSize: LIST_LIMIT });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [BTN_ACTS.SAVE]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [CARD_NAME]: saveErr,
      },
    });
  }
};

const HandleClearData: Handler<Params<"CLEAR">> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    AddNedDrugOrderItemSequence: {
      ...state.AddNedDrugOrderItemSequence,
      searchDrugOrder: {
        ...FILTER_INIT,
      },
      drugOrderList: [],
      paginatedItems: {
        ...PAGINATED_INIT,
      },
    },
  });
};

/**========================================================================
 **                               APIs
 *========================================================================**/

const GetDrugOrderNedList: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await DrugOrderItemNedList.list({
    apiToken: controller.apiToken,
    params,
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp;
};

const SaveAddNedDrugOrderItem: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await DrugOrderItemEditNed.put({
    apiToken: controller.apiToken,
    data: {
      items: params?.selectedItems || [],
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return [null, err];

  return [resp, null];
};

const GetDrugOrderTypeOptions: Handler = async (controller, params) => {
  const state = controller.getState();

  const [resp, err] = await ChoiceView.get({
    apiToken: controller.apiToken,
    params: {
      model: "TPD.DrugOrder",
      field: "type"
    },
    extra: {
      division: controller.data.division,
    },
  });

  if (err) return null;

  return resp?.items;
};